import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  Typography, Button, Table, Tag, Select, DatePicker, Space, message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import AbsenceCreateModal from '../components/AbsenceCreateModal';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'Submitted',    value: 'Submitted'    },
  { label: 'Acknowledged', value: 'Acknowledged' },
  { label: 'Canceled',    value: 'Canceled'    },
];

const REASON_OPTIONS = [
  'Sick/Health Related', 'Jury Duty', 'Bereavement', 'Outage', 'Other',
].map((v) => ({ label: v, value: v }));

const STATUS_COLOR = { Submitted: 'blue', Acknowledged: 'green', Canceled: 'default' };


let _cache = null;

export default function MyAbsenceNotificationsPage() {
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);

  const restoredRef = useRef(!!_cache);
  const [rows, setRows] = useState(() => _cache?.rows           ?? []);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => _cache?.statusFilter ?? []);
  const [reasonFilter, setReasonFilter] = useState(() => _cache?.reasonFilter ?? '');
  const [dateRange, setDateRange] = useState(() => _cache?.dateRange ?? [dayjs().subtract(7, 'day'), dayjs().add(30, 'day')]);

  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mine: '1' };
      if (statusFilter.length)  params.status         = statusFilter.join(',');
      if (reasonFilter)         params.reason         = reasonFilter;
      if (dateRange?.[0])       params.start_date     = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1])       params.end_date       = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/api/absences', { params });
      setRows(res.data);
      _cache = { rows: res.data, statusFilter, reasonFilter, dateRange };
    } catch {
      message.error('Failed to load absence notifications.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reasonFilter, dateRange]);

  useEffect(() => {
    if (restoredRef.current) { restoredRef.current = false; return; }
    load();
  }, [load]);


  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 65 },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 130, ellipsis: true,
      render: (v) => v || '—' },
    { title: 'Reason', dataIndex: 'Reason', key: 'Reason', ellipsis: true },
    { title: 'Period', dataIndex: 'AbsencePeriod', key: 'AbsencePeriod', ellipsis: true },
    { title: 'Date Start', dataIndex: 'DateStart', key: 'DateStart', width: 110 },
    { title: 'Date End', dataIndex: 'DateEnd', key: 'DateEnd', width: 110 },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Submitted', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175, render: (v) => formatTS(v) },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>My Absence Notifications</Typography.Title>
        {/* A View Only user (Admin > Users) does not submit absences. */}
        {!currentUser?.IsViewOnly && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Submit Absence
          </Button>
        )}
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          mode="multiple" allowClear placeholder="All statuses"
          options={STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter}
          style={{ minWidth: 180 }}
        />
        <Select
          allowClear placeholder="Reason"
          options={REASON_OPTIONS}
          value={reasonFilter || undefined}
          onChange={(v) => setReasonFilter(v || '')}
          style={{ minWidth: 200 }}
        />
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          format="YYYY-MM-DD"
          placeholder={['Date From', 'Date To']}
        />
        <Button icon={<SearchOutlined />} onClick={() => { _cache = null; load(); }} loading={loading}>
          Search
        </Button>
      </Space>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: false,
          showTotal: (t) => `${t} record${t === 1 ? '' : 's'}`,
        }}
        onRow={(r) => ({ onClick: () => navigate(`/absences/${r.ID}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'No absence notifications found.' }}
      />

      <AbsenceCreateModal
        open={createOpen}
        userId={currentUser?.ID}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { _cache = null; setCreateOpen(false); load(); }}
      />
    </>
  );
}
